/*!
 * Created by netlogix GmbH & Co. KG
 *
 * @copyright netlogix GmbH & Co. KG
 */

import { DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup, ValidationErrors,
  Validators
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { State } from '@core/store';
import { Store } from '@ngrx/store';
import { Tour } from '@shared/models';
import { autocompleteOptionSelectedValidator } from '@shared/validators/autocomplete-option-selected.validator';
import * as TourActions from '@tours/store/tours.actions';
import { getCurrentTour, isActionPending } from '@tours/store/tours.selectors';
import { TeamForm } from '@tours/tour-detail/form-drivers/_models/team-form.model';
import * as UserActions from '@users/store/users.actions';
import { Observable, Subscription } from 'rxjs';
import { filter, map, startWith, tap } from 'rxjs/operators';

@Component({
    selector: 'app-tour-detail',
    templateUrl: './tour-detail.component.html',
    styleUrls: ['./tour-detail.component.scss'],
    standalone: false
})
export class TourDetailComponent implements OnInit, OnDestroy {
  actionPending$: Observable<boolean>;
  tour$: Observable<Tour>;
  tourForm: UntypedFormGroup;
  tourDate$: Observable<Date>;

  private routeSubscription: Subscription;
  private tourSubscription: Subscription;

  constructor(
    private store: Store<State>,
    private route: ActivatedRoute,
    private formBuilder: UntypedFormBuilder,
    private datePipe: DatePipe
  ) { }

  ngOnInit(): void {
    this.initTourForm();
    this.addDateToNewTour();
    this.loadTourFromParameter();

    this.tour$ = this.store.select(getCurrentTour);
    this.actionPending$ = this.store.select(isActionPending);

    this.store.dispatch(new UserActions.LoadUsers());

    this.fillFormWithExistingData();
  }

  private initTourForm(): void {
    this.tourForm = this.formBuilder.group({
      '@id': [''],
      customer: [null, Validators.compose([Validators.required, autocompleteOptionSelectedValidator])],
      constructionSite: [{value: '', disabled: true}, Validators.compose([Validators.required, autocompleteOptionSelectedValidator])],
      material: [''],
      internalNote: [''],
      teams: this.formBuilder.array([]),
      tourType: ['', Validators.required],
      contact: [''],
      supervisor: [''],
      manager: ['', (control: FormControl) => this.validateManager(control)],
      appointment: this.formBuilder.group({
        '@id': [null],
        class: [null],
        date: ['', Validators.required],
        startTime: ['']
      }),
      reminder: this.formBuilder.group({
        '@id': [null],
        date: [''],
        time: [''],
        dateTime: [''],
        text: [''],
        active: [true]
      })
    });
  }

  private addDateToNewTour(): void {
    this.route.queryParams.subscribe(
      params => {
        if (Object.prototype.hasOwnProperty.call(params, 'startDate')) {
          this.tourForm.get('appointment.date').patchValue(new Date(params.startDate));
        }
      }
    );
  }

  private loadTourFromParameter(): void {
    this.routeSubscription = this.route.params.pipe(
      map(params => params.tourId),
      tap(tourId => this.store.dispatch(new TourActions.LoadTour(tourId)))
    ).subscribe();
  }

  private fillFormWithExistingData(): void {
    this.tourSubscription = this.tour$.pipe(
      filter(tour => !!tour)
    ).subscribe(tour => {
      this.tourForm.patchValue(this.removeReminder(tour));
      if (tour.reminder) {
        this.tourForm.get('reminder').patchValue({
          ...tour.reminder,
          date: this.datePipe.transform(tour.reminder.dateTime, 'yyyy-MM-dd'),
          time: this.datePipe.transform(tour.reminder.dateTime, 'HH:mm')
        });
      }

      const teamFormElements = [];
      if (Object.prototype.hasOwnProperty.call(tour, 'teams')) {
        for (const team of tour.teams) {
          teamFormElements.push(this.formBuilder.group(new TeamForm(team)));
        }
      }
      this.tourForm.setControl('teams', new UntypedFormArray(teamFormElements));
      const currentDate = this.tourForm.get('appointment.date').value;
      this.tourDate$ = this.tourForm.get('appointment.date').valueChanges.pipe(
        startWith(currentDate ? new Date(currentDate) : null)
      );
      if (tour['@id'] !== null) {
        this.tourForm.get('customer').disable();
      }
    });
  }

  private removeReminder(tour: Tour): Tour {
    const cleanedTour = Object.assign({ }, tour);
    delete cleanedTour.reminder;
    return cleanedTour;
  }

  persistTour(): void {
    const reminderForm = this.tourForm.get('reminder');
    const reminderPristine = reminderForm.pristine;

    if (!reminderPristine) {
      reminderForm.patchValue({
        dateTime: `${new Date(reminderForm.value.date).toDateString()} ${reminderForm.value.time}`
      });
    }

    if (this.tourForm.invalid) {
      return;
    }

    // Keep the reminder control in the form (the Erinnerung tab binds to it via
    // [formGroup]); only drop an untouched reminder from the persisted payload.
    const tourValue = { ...this.tourForm.value };
    if (reminderPristine) {
      delete tourValue.reminder;
    }

    this.store.dispatch(new TourActions.PersistTour(tourValue));
  }

  validateManager(control: FormControl): ValidationErrors | null {
    if (control.value['@id'] === undefined) {
      return { user: false };
    }

    return null;
  }

  ngOnDestroy(): void {
    this.routeSubscription.unsubscribe();
    if (this.tourSubscription) {
      this.tourSubscription.unsubscribe();
    }
  }
}
